import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  toNextPaymentDue,
  toStatementPayment,
  toStatementSummary,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.mapping'
import {
  ACCOUNT_STATEMENTS_DEMO,
  type AccountStatementsData,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import {
  type CardManagementSource,
  isUnprovisioned,
  resolveCardManagementSource,
} from 'src/page-components/Accounts/cardManagementSource'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * Single data seam for the card-detail Statements tab (EX-40600).
 *
 * Mirrors `useCardDetailHeroData` for the sibling Card Management surface, and
 * carries the same rule: section components MUST read through this hook rather
 * than importing `ACCOUNT_STATEMENTS_DEMO` directly.
 *
 * ## Real data, with the demo figures as a per-group fallback
 *
 * Three endpoints back this tab, across two frozen contracts:
 *
 * - `GET /creditcards/{id}/cardmanagement/payments/summary` — next amount due
 * - `GET /creditcards/{id}/cardmanagement/payments` — payment history
 * - `GET /creditcards/{id}/cardmanagement/statements` — closed statements
 *
 * (Statement LINE ITEMS are a fourth route, fetched lazily per statement when a
 * detail modal opens — see `useStatementTransactions`. Loading them here would
 * cost one request per row on tab load for ledgers almost nobody opens.)
 *
 * The return groups by SOURCE, not by consumer, and that grouping is what makes
 * the fallback safe to apply piecemeal: `payments` and `statements` are
 * separately fetched, so each falls back on its own and a statements failure
 * cannot swap out figures the payments endpoint answered correctly. Sections
 * destructure only the group they use.
 *
 * ## When the fallback applies
 *
 * Per group, via the shared `resolveCardManagementSource` — the same rule the
 * Activity tab uses, so the two tabs on this page cannot drift apart on it:
 *
 * - `loading` while the reads are in flight. NOT demo: painting fabricated
 *   balances for a frame and replacing them is how invented figures come to
 *   look real.
 * - `demo` when the read succeeds with nothing, or fails as unprovisioned
 *   (`403` not enrolled / `404` no route or card). Both mean "there is nothing
 *   here", which is the whole reason the demo surface exists — and matters
 *   today because Statements' and Payments' controllers are registered only
 *   for the `dev` and `stage` Spring profiles.
 * - `error` for a genuine failure, with a retry. A `503` is a transient
 *   outage; substituting fabricated figures for it would misreport the card as
 *   known-and-empty.
 *
 * Successful mock responses carry `X-Extend-Card-Management-Mock: true`. A 200
 * is not by itself evidence of an authoritative statement ledger.
 *
 * WHEN A GROUP IS ON ITS FALLBACK, ITS FIGURES ARE FABRICATED — the tab badges
 * each section independently, because an invented balance presented as a real
 * one is the specific failure this tab has to avoid. Keep the exposure in mind
 * when changing the gates: this tab is opened by the server capability
 * `READ_PHYSICAL_CARD_STATEMENTS` as well as by a client flag (see
 * `isUpdatedCardStatementsEnabled`), so enrolling an organization on the
 * backend is by itself enough to reach it.
 */

/**
 * How many statements the list shows before "See more statements".
 *
 * The wire has no `recent`/`older` split — that division is purely this tab's
 * progressive-disclosure affordance, so it is applied to the fetched page here
 * rather than being asked of the server. Matches the demo's four.
 */
const RECENT_STATEMENT_COUNT = 4

/**
 * One page is requested, and the tab does not paginate further.
 *
 * "See more statements" reveals the already-fetched remainder in place; it is
 * not a load-more control, and wiring it to the cursor would change a
 * zero-latency disclosure into a network round trip. 20 is the contract's
 * default page size and comfortably more than the eight the design shows.
 */
const STATEMENT_PAGE_LIMIT = 20

/** Payment history is a short list in the design; one page is the whole section. */
const PAYMENT_PAGE_LIMIT = 20

export interface AccountStatementsResult extends AccountStatementsData {
  /**
   * What the `payments` group above actually is. `demo` while the fallback is
   * showing, `loading` until the reads resolve — the tab renders a loading
   * state rather than demo figures it is about to replace, which previously
   * flashed fabricated balances for a frame on every open.
   */
  paymentsState: CardManagementSource
  /** Same, for `statements`. The two resolve independently. */
  statementsState: CardManagementSource
  /** Re-requests the payment summary and history. */
  retryPayments: () => void
  /** Re-requests the statement list. */
  retryStatements: () => void
}

/**
 * @param creditCardId - The card whose statements to read. `undefined` while
 *   the card is still loading; the queries stay disabled until it resolves.
 */
export const useAccountStatementsData = (
  creditCardId: string | undefined,
): AccountStatementsResult => {
  const summaryQuery = useQuery(
    physicalCardManagementQueries.paymentSummary(creditCardId),
  )
  const paymentsQuery = useQuery(
    physicalCardManagementQueries.payments({
      creditCardId,
      limit: PAYMENT_PAGE_LIMIT,
    }),
  )
  const statementsQuery = useQuery(
    physicalCardManagementQueries.statements({
      creditCardId,
      limit: STATEMENT_PAGE_LIMIT,
    }),
  )

  const summary = summaryQuery.data
  const paymentItems = paymentsQuery.data?.items
  const statementItems = statementsQuery.data?.items

  const payments = useMemo(() => {
    if (!summary && !paymentItems) {
      return null
    }

    const nextPaymentDue = summary
      ? toNextPaymentDue(summary.nextPaymentDue)
      : null
    const history = paymentItems?.map(toStatementPayment) ?? []

    /*
     * Live when EITHER half answered — not both.
     *
     * Requiring both sent ordinary card states to the demo fallback, and in the
     * worst direction. A first billing cycle has a real next payment and an
     * empty history; a fully paid-up card has real history and `null` next
     * payment. Both used to render a FABRICATED amount and due date over a live
     * read — showing money owed on a card that owes nothing, and replacing the
     * single most consequential figure on the tab.
     *
     * `nextPaymentDue: null` is a real answer (see `toNextPaymentDue`), and the
     * section renders its settled state for it. Only a card where neither
     * endpoint said anything is genuinely empty.
     *
     * The two share one verdict because they are one visual section — badging
     * half of it would be unreadable — but the model now carries the settled
     * case, so one verdict no longer costs the other half's data.
     */
    return nextPaymentDue === null && history.length === 0
      ? null
      : { nextPaymentDue, history }
  }, [summary, paymentItems])

  const statements = useMemo(() => {
    if (!statementItems) {
      return null
    }
    // A cursor counts as data even with no items on this page: `nextCursor` is
    // the contract's sole terminal signal and a non-final page may legitimately
    // be empty. Fabricating over a response that says more exists is the wrong
    // way to be wrong, even though this tab reads only one page.
    if (statementItems.length === 0 && !statementsQuery.data?.nextCursor) {
      return null
    }

    const mapped = statementItems.map(toStatementSummary)

    return {
      recent: mapped.slice(0, RECENT_STATEMENT_COUNT),
      older: mapped.slice(RECENT_STATEMENT_COUNT),
    }
  }, [statementItems, statementsQuery.data?.nextCursor])

  // Grouped by SOURCE, so each resolves on its own: a statements outage cannot
  // blank or fabricate payment figures the payments endpoints answered.
  const paymentsState = resolveCardManagementSource({
    isLoading:
      summaryQuery.isLoading || paymentsQuery.isLoading || !creditCardId,
    isError: summaryQuery.isError || paymentsQuery.isError,
    // A GENUINE failure from either half outranks an unprovisioned answer from
    // the other. `??` alone let summary's verdict win whenever both failed, so
    // an unprovisioned summary (`403`) paired with a real outage on payments
    // (`503`) resolved to `demo` — fabricated figures rendered over an outage,
    // which is precisely what `resolveCardManagementSource` refuses to do,
    // reached by a different route.
    error:
      [summaryQuery.error, paymentsQuery.error].find(
        (queryError) => queryError && !isUnprovisioned(queryError),
      ) ??
      summaryQuery.error ??
      paymentsQuery.error,
    hasData: payments !== null,
  })

  const statementsState = resolveCardManagementSource({
    isLoading: statementsQuery.isLoading || !creditCardId,
    isError: statementsQuery.isError,
    error: statementsQuery.error,
    hasData: statements !== null,
  })

  // Destructured because React Query returns a NEW result object every render.
  // Closing over the queries themselves would give these callbacks a fresh
  // identity on every render and make the `useCallback`s inert; `refetch` is
  // bound to the observer and stable. Same fix as the Activity hook.
  const { refetch: refetchSummary } = summaryQuery
  const { refetch: refetchPayments } = paymentsQuery
  const { refetch: refetchStatements } = statementsQuery

  const retryPayments = useCallback(() => {
    void refetchSummary()
    void refetchPayments()
  }, [refetchSummary, refetchPayments])

  const retryStatements = useCallback(() => {
    void refetchStatements()
  }, [refetchStatements])

  return {
    // Always populated so consumers never handle `undefined`; `paymentsState`
    // is what says whether these figures are real, fabricated, or placeholder
    // values behind a loading or error render.
    payments: payments ?? ACCOUNT_STATEMENTS_DEMO.payments,
    statements: statements ?? ACCOUNT_STATEMENTS_DEMO.statements,
    paymentsState,
    statementsState,
    retryPayments,
    retryStatements,
  }
}
